'use client';

import React from 'react';
import { motion } from 'framer-motion';

import { Box, BoxProps } from '../box';
import { VStack } from '../stack';
import { Tabs } from '../tabs';
import { Skeleton } from '../skeleton';

import { DetailHeroHeader, DetailHeroHeaderProps } from './detail-hero-header';
import { DetailQuickStats, QuickStatItem } from './detail-quick-stats';
import { DetailSection, DetailSectionProps } from './detail-section';

// Motion div for animations (using native div to avoid Chakra prop conflicts)
const MotionDiv = motion.div;

export interface TabConfig {
  /** Tab ID */
  id: string;
  /** Tab label */
  label: string;
  /** Tab badge count */
  badge?: number | string;
  /** Tab content */
  content: React.ReactNode;
  /** Tab icon */
  icon?: React.ReactNode;
}

export interface ProfessionalDetailViewProps extends Omit<BoxProps, 'children'> {
  /** Hero header configuration */
  header: Omit<DetailHeroHeaderProps, 'children'>;
  /** Quick stats (displayed below header) */
  quickStats?: QuickStatItem[];
  /** Main content sections */
  sections?: Array<Omit<DetailSectionProps, 'children'> & { content: React.ReactNode }>;
  /** Sidebar sections */
  sidebarSections?: Array<Omit<DetailSectionProps, 'children'> & { content: React.ReactNode }>;
  /** Tab configuration (if using tabs instead of sections) */
  tabs?: TabConfig[];
  /** Default active tab */
  defaultTab?: string;
  /** Sidebar position */
  sidebarPosition?: 'left' | 'right';
  /** Sidebar width */
  sidebarWidth?: string | number;
  /** Show sidebar */
  showSidebar?: boolean;
  /** Layout mode */
  layoutMode?: 'sections' | 'tabs' | 'mixed';
  /** Loading state */
  isLoading?: boolean;
  /** Gap between sections */
  sectionGap?: number | string;
  /**
   * Number of columns for the main sections grid (responsive: 1 column below
   * `lg`). Defaults to 1 (single stacked column). Set to 2 to lay sections out
   * side-by-side and cut vertical scroll on section-heavy detail pages.
   */
  sectionColumns?: number;
  /** Custom content below header */
  headerContent?: React.ReactNode;
  /** Custom content after sections */
  footerContent?: React.ReactNode;
}

/**
 * ProfessionalDetailView - Complete professional detail page layout
 * Combines hero header, quick stats, sections, and tabs into a cohesive view
 */
export const ProfessionalDetailView: React.FC<ProfessionalDetailViewProps> = ({
  header,
  quickStats,
  sections = [],
  sidebarSections = [],
  tabs = [],
  defaultTab,
  sidebarPosition = 'right',
  sidebarWidth = '350px',
  showSidebar = true,
  layoutMode = 'sections',
  isLoading = false,
  sectionGap = 6,
  sectionColumns = 1,
  headerContent,
  footerContent,
  ...boxProps
}) => {
  const [activeTab, setActiveTab] = React.useState(defaultTab || tabs[0]?.id || '');
  const hasSidebar = showSidebar && sidebarSections.length > 0;

  // Destructure header to extract title explicitly for TypeScript
  const { title: headerTitle, ...headerRest } = header;

  // Animation variants
  const pageVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
      },
    },
  };

  // Loading skeleton
  if (isLoading) {
    return (
      <Box {...boxProps}>
        <VStack gap={sectionGap} align="stretch">
          {/* Header skeleton */}
          <Box
            padding={6}
            borderRadius="xl"
            backgroundColor="bg.surface"
            border="1px solid"
            borderColor="border.muted"
          >
            <Skeleton width="200px" height="32px" marginBottom={2} />
            <Skeleton width="300px" height="20px" marginBottom={4} />
            <Skeleton width="100%" height="80px" />
          </Box>

          {/* Quick stats skeleton */}
          <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={4}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height="80px" borderRadius="lg" />
            ))}
          </Box>

          {/* Content skeleton */}
          <Box
            display="grid"
            gridTemplateColumns={hasSidebar ? `1fr ${sidebarWidth}` : '1fr'}
            gap={6}
          >
            <Box>
              <VStack gap={4} align="stretch">
                <Skeleton height="200px" borderRadius="xl" />
                <Skeleton height="200px" borderRadius="xl" />
              </VStack>
            </Box>
            {hasSidebar && (
              <Box>
                <Skeleton height="300px" borderRadius="xl" />
              </Box>
            )}
          </Box>
        </VStack>
      </Box>
    );
  }

  // Render sections
  const renderSections = (sectionList: typeof sections, delay = 0) =>
    sectionList.map((section, index) => {
      const { content, ...sectionProps } = section;
      const { title, ...restProps } = sectionProps;
      return (
        <DetailSection
          key={section.title || index}
          title={title}
          {...restProps}
          staggerDelay={delay + index * 0.1}
        >
          {content}
        </DetailSection>
      );
    });

  // Render tabs content
  const renderTabs = () => (
    <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
      <Tabs.List>
        {tabs.map((tab) => (
          <Tabs.Trigger key={tab.id} value={tab.id}>
            {tab.icon && <Box marginRight={2}>{tab.icon}</Box>}
            {tab.label}
            {tab.badge !== undefined && (
              <Box
                marginLeft={2}
                paddingX={2}
                paddingY={0.5}
                borderRadius="full"
                backgroundColor="bg.subtle"
                fontSize="xs"
                fontWeight="medium"
              >
                {tab.badge}
              </Box>
            )}
          </Tabs.Trigger>
        ))}
      </Tabs.List>

      {tabs.map((tab) => (
        <Tabs.Content key={tab.id} value={tab.id}>
          <MotionDiv
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
            style={{ paddingTop: '16px' }}
          >
            {tab.content}
          </MotionDiv>
        </Tabs.Content>
      ))}
    </Tabs.Root>
  );

  // Main content based on layout mode
  const renderMainContent = () => {
    switch (layoutMode) {
      case 'tabs':
        return renderTabs();
      case 'mixed':
        return (
          <VStack gap={sectionGap} align="stretch">
            {sections.length > 0 && renderSections(sections)}
            {tabs.length > 0 && renderTabs()}
          </VStack>
        );
      default: // sections
        if (sectionColumns > 1) {
          // Responsive multi-column grid: single column below `lg`, then
          // `sectionColumns` side-by-side. Items align to the top so cards of
          // different heights don't stretch.
          return (
            <Box
              display="grid"
              gridTemplateColumns={{ base: '1fr', lg: `repeat(${sectionColumns}, 1fr)` }}
              gap={sectionGap}
              alignItems="start"
            >
              {renderSections(sections)}
            </Box>
          );
        }
        return (
          <VStack gap={sectionGap} align="stretch">
            {renderSections(sections)}
          </VStack>
        );
    }
  };

  return (
    <MotionDiv variants={pageVariants} initial="hidden" animate="visible">
      <Box {...boxProps}>
        <VStack gap={sectionGap} align="stretch">
          {/* Hero Header with optional quick stats inside */}
          <DetailHeroHeader title={headerTitle} {...headerRest} isLoading={isLoading}>
            {quickStats && quickStats.length > 0 && <DetailQuickStats stats={quickStats} />}
          </DetailHeroHeader>

          {/* Custom header content */}
          {headerContent && <MotionDiv variants={sectionVariants}>{headerContent}</MotionDiv>}

          {/* Main content area */}
          {hasSidebar ? (
            <Box
              display="grid"
              gridTemplateColumns={{
                base: '1fr',
                lg: sidebarPosition === 'left' ? `${sidebarWidth} 1fr` : `1fr ${sidebarWidth}`,
              }}
              gap={6}
            >
              {/* Sidebar - Left position */}
              {sidebarPosition === 'left' && (
                <Box display={{ base: 'none', lg: 'block' }}>
                  <VStack gap={sectionGap} align="stretch" position="sticky" top={4}>
                    {renderSections(sidebarSections, 0.3)}
                  </VStack>
                </Box>
              )}

              {/* Main content */}
              <Box>{renderMainContent()}</Box>

              {/* Sidebar - Right position */}
              {sidebarPosition === 'right' && (
                <Box display={{ base: 'none', lg: 'block' }}>
                  <VStack gap={sectionGap} align="stretch" position="sticky" top={4}>
                    {renderSections(sidebarSections, 0.3)}
                  </VStack>
                </Box>
              )}

              {/* Mobile sidebar (below main content) */}
              <Box display={{ base: 'block', lg: 'none' }} gridColumn="1 / -1">
                <VStack gap={sectionGap} align="stretch">
                  {renderSections(sidebarSections, 0.3)}
                </VStack>
              </Box>
            </Box>
          ) : (
            renderMainContent()
          )}

          {/* Footer content */}
          {footerContent && <MotionDiv variants={sectionVariants}>{footerContent}</MotionDiv>}
        </VStack>
      </Box>
    </MotionDiv>
  );
};

export default ProfessionalDetailView;
